import { useEffect } from "react";
import { useDataAvailability } from "./useDataAvailability";
import { ProductClass } from "../types/enums";

interface YearDefaultUpdates {
  year?: number;
  startYear?: number;
  endYear?: number;
}

interface UseYearDefaultsProps {
  year?: number;
  startYear?: number;
  endYear?: number;
  productClass: ProductClass;
  setQuery: (params: YearDefaultUpdates) => void;
  needsYear?: boolean;
  needsYearRange?: boolean;
}

/**
 * Hook to automatically set year defaults based on data availability
 * when years are not specified in URL params.
 *
 * Prefer setting defaults via the Zod param schemas in paramSchemas.ts
 * rather than calling this hook in connected chart components.
 */
export const useYearDefaults = ({
  year,
  startYear,
  endYear,
  productClass,
  setQuery,
  needsYear = false,
  needsYearRange = false,
}: UseYearDefaultsProps) => {
  const { getYearRangeForProductClass, loading } = useDataAvailability();

  useEffect(() => {
    if (loading) return;

    const yearRange = getYearRangeForProductClass(productClass);
    if (!yearRange) return;

    const updates: YearDefaultUpdates = {};

    // Set default year if needed and not already set
    if (needsYear && year === undefined) {
      updates.year = yearRange.yearMax;
    }

    // Set default year range if needed and not already set,
    // or clamp to valid range if current value is out of bounds
    if (needsYearRange) {
      if (startYear === undefined || startYear < yearRange.yearMin) {
        updates.startYear = yearRange.yearMin;
      }
      if (endYear === undefined || endYear > yearRange.yearMax) {
        updates.endYear = yearRange.yearMax;
      }
    }

    // Only call setQuery if there are updates
    if (Object.keys(updates).length > 0) {
      setQuery(updates);
    }
  }, [
    loading,
    year,
    startYear,
    endYear,
    productClass,
    needsYear,
    needsYearRange,
    getYearRangeForProductClass,
    setQuery,
  ]);
};
